<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import InputFieldGroup from './InputFieldGroup.vue';
import RequiredLabel from './RequiredLabel.vue';
import ErrorSlot from './ErrorSlot.vue';

const props = defineProps<{
  label: string;
  errorText?: string | null;
  required?: boolean;
  modelValue: number | null;
  placeholder?: string;
  min?: number;
  max?: number;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

function format(value: number | null): string {
  if (value == null || value <= 0) return '';
  return String(Math.trunc(value));
}

const inputValue = ref<string>(format(props.modelValue));
let lastSynced = inputValue.value;

watch(
  () => props.modelValue,
  (value) => {
    const formatted = format(value);
    if (formatted !== lastSynced && formatted !== inputValue.value) {
      inputValue.value = formatted;
      lastSynced = formatted;
    } else if (formatted === inputValue.value) {
      lastSynced = formatted;
    }
  },
);

function onInput(e: Event): void {
  const target = e.target as HTMLInputElement;
  const cleaned = target.value.replace(/[^0-9]/g, '');
  target.value = cleaned;
  lastSynced = cleaned;
  emit('update:modelValue', cleaned === '' ? 0 : Number(cleaned));
}

function clamp(next: number): number {
  const min = props.min ?? 0;
  const max = props.max ?? 120;
  if (next < min) return min;
  if (next > max) return max;
  return next;
}

function decrement(): void {
  emit('update:modelValue', clamp((props.modelValue ?? 0) - 1));
}

function increment(): void {
  emit('update:modelValue', clamp((props.modelValue ?? 0) + 1));
}

const hasError = computed(() => props.errorText != null);
</script>

<template>
  <div class="block" data-testid="input-age">
    <RequiredLabel
      :text="label"
      :required="required ?? false"
      :has-error="hasError"
    />
    <InputFieldGroup
      :has-error="hasError"
      stepper="plusminus"
      @increment="increment"
      @decrement="decrement"
    >
      <input
        v-model="inputValue"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        class="flex-1 min-w-0 h-10 px-2.5 bg-transparent outline-none text-body-medium text-text-primary placeholder:text-text-tertiary"
        :placeholder="placeholder"
        @input="onInput"
      />
    </InputFieldGroup>
    <ErrorSlot :text="errorText ?? null" />
  </div>
</template>
